import { promptModelFamily } from './prompt-llm-models.js';
import type { LlmListPriceRates } from './llm-model-list-prices.js';

/**
 * Claude and Gemini tokenizers disagree by about this much on the same text.
 * Switching Gemini → Claude multiplies tokens by the factor; the other way
 * divides. Same family leaves the counts alone.
 */
export const CROSS_FAMILY_TOKENIZER_FACTOR = 1.3;

/** Share of produced tokens treated as thinking when the caller does not say. */
export const DEFAULT_THINKING_SHARE = 0.25;

/**
 * Image models bill each generated image as this many output tokens (a 1K
 * image). The list-price output rate is the per-image price divided by it.
 */
export const IMAGE_OUTPUT_TOKENS = 1120;

const MICROS_PER_MILLION = 1_000_000;

export interface ProjectCostInput {
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheWriteTokens?: number;
  cacheReadTokens?: number;
  /** Successful calls. An image feature treats each one as one generated image. */
  calls: number;
  fromModel: string;
  toModel: string;
  rates: LlmListPriceRates;
  /**
   * Fraction of produced tokens billed as thinking, 0–1. Absent uses the
   * observed share, or {@link DEFAULT_THINKING_SHARE} when nothing was produced.
   */
  thinkingShare?: number;
  isImage?: boolean;
}

export interface ProjectedCost {
  costMicros: number;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  /** Set for an image projection: what one image costs at the target rate. */
  perImageMicros: number | null;
}

/** Opus 4.7+, Sonnet 5, Opus 5.x, and Haiku 5 use a denser Claude tokenizer than 4.x. */
export function isModernClaudeTokenizerModel(modelId: string): boolean {
  const id = modelId.trim().toLowerCase();
  if (id.includes('sonnet-5')) return true;
  if (id.includes('opus-5')) return true;
  if (id.includes('opus-4-7')) return true;
  if (id.includes('haiku-5')) return true;
  return false;
}

function claudeTokenizerGenerationFactor(fromModel: string, toModel: string): number {
  const fromModern = isModernClaudeTokenizerModel(fromModel);
  const toModern = isModernClaudeTokenizerModel(toModel);
  if (fromModern === toModern) return 1;
  if (!fromModern && toModern) return CROSS_FAMILY_TOKENIZER_FACTOR;
  return 1 / CROSS_FAMILY_TOKENIZER_FACTOR;
}

export function tokenizerFactor(fromModel: string, toModel: string): number {
  const from = promptModelFamily(fromModel);
  const to = promptModelFamily(toModel);
  if (!from || !to) return 1;
  if (from === 'claude' && to === 'claude') {
    return claudeTokenizerGenerationFactor(fromModel, toModel);
  }
  if (from === to) return 1;
  if (from === 'gemini' && to === 'claude') return CROSS_FAMILY_TOKENIZER_FACTOR;
  return 1 / CROSS_FAMILY_TOKENIZER_FACTOR;
}

export function isImageModel(model: string): boolean {
  const id = model.trim().toLowerCase();
  return id.includes('-image') || id.includes('image-') || id.includes('nano-banana');
}

/** Integer micro-dollars. Truncation matches the usage repo's cost SQL. */
function toMicros(tokens: number, perMillion: number): number {
  return Math.floor((tokens * perMillion) / MICROS_PER_MILLION);
}

function clampShare(share: number): number {
  if (Number.isNaN(share)) return DEFAULT_THINKING_SHARE;
  return Math.min(1, Math.max(0, share));
}

/**
 * What the same work would have cost on another model.
 *
 * Text scales input and produced tokens by the tokenizer factor, then splits
 * the produced tokens by the thinking share. Images ignore the tokenizer and
 * bill {@link IMAGE_OUTPUT_TOKENS} output tokens per call.
 */
export function projectCost(input: ProjectCostInput): ProjectedCost {
  if (input.isImage) {
    const calls = Math.max(0, input.calls);
    const outputTokens = calls * IMAGE_OUTPUT_TOKENS;
    const perImageMicros = toMicros(IMAGE_OUTPUT_TOKENS, input.rates.outputPerMillion);
    return {
      costMicros: toMicros(outputTokens, input.rates.outputPerMillion),
      inputTokens: 0,
      outputTokens,
      thinkingTokens: 0,
      perImageMicros,
    };
  }

  const factor = tokenizerFactor(input.fromModel, input.toModel);
  const inputTokens = Math.round(Math.max(0, input.inputTokens) * factor);
  const cacheWriteTokens = Math.round(Math.max(0, input.cacheWriteTokens ?? 0) * factor);
  const cacheReadTokens = Math.round(Math.max(0, input.cacheReadTokens ?? 0) * factor);
  const produced = Math.max(0, input.outputTokens) + Math.max(0, input.thinkingTokens);
  const scaled = Math.round(produced * factor);
  const observedShare = produced > 0 ? input.thinkingTokens / produced : DEFAULT_THINKING_SHARE;
  const share = clampShare(input.thinkingShare ?? observedShare);
  const thinkingTokens = Math.round(scaled * share);
  const outputTokens = scaled - thinkingTokens;

  return {
    costMicros:
      toMicros(inputTokens, input.rates.inputPerMillion) +
      toMicros(outputTokens, input.rates.outputPerMillion) +
      toMicros(thinkingTokens, input.rates.thinkingPerMillion) +
      toMicros(cacheWriteTokens, input.rates.cacheWritePerMillion) +
      toMicros(cacheReadTokens, input.rates.cacheReadPerMillion),
    inputTokens,
    outputTokens,
    thinkingTokens,
    perImageMicros: null,
  };
}
